import React from 'react';
import { ActionIcon, NumberInput, Select, Table, TextInput } from '@mantine/core';
import { inject, observer } from 'mobx-react';
import { IconTestPipe, IconTrash } from '@tabler/icons-react';
import { t } from 'i18next';
import { CalendarDateInput } from '../../../global/component/molecules/CalendarDateInput';
import { ITestKitOrderLine } from '../../../global/entities/TestKitOrder/TestKitOrder.ts';
import { useGetTestKitOrderNew } from '../../../global/entities/TestKitOrder/queries/useGetTestKitOrderNew.tsx';

interface ITestKitFormRowProps {
    row: ITestKitOrderLine;
    key: number;
    onClickDelete: (id: number) => void;
    setRows: React.Dispatch<React.SetStateAction<ITestKitOrderLine[]>>;
    errors: any;
}

const TestKitFormRow: React.FC<ITestKitFormRowProps> = inject()(
    observer(({ row, key, onClickDelete, setRows, errors }) => {
        const { data: testKitOrderNew } = useGetTestKitOrderNew();

        const handleChange = (field: keyof ITestKitOrderLine, value: any) => {
            setRows((prevRows: ITestKitOrderLine[]) =>
                prevRows.map((el) => (el.id === row.id ? { ...el, [field]: value } : el)),
            );
        };

        return (
            <Table.Tr key={key}>
                <Table.Td>
                    <Select
                        placeholder={t('modal.testKitModal.enterTestKitType')}
                        leftSection={<IconTestPipe size={16} />}
                        data={testKitOrderNew?.testKitTypesCombobox}
                        onChange={(value) => {
                            handleChange('testKitTypeID', Number(value));
                            const price =
                                testKitOrderNew?.testKitTypes.find((el) => el.id === Number(value))?.price || 0;
                            handleChange('price', price);
                        }}
                        value={row?.testKitTypeID > 0 ? row?.testKitTypeID.toString() : null}
                        error={errors.testKitTypeID}
                        tabIndex={-1}
                        miw={200}
                    />
                </Table.Td>
                <Table.Td>
                    <TextInput
                        value={row?.sampleNumber}
                        maxLength={20}
                        onChange={(event) => handleChange('sampleNumber', event.currentTarget.value)}
                        error={row?.sampleNumber ? undefined : errors?.sampleNumber}
                    />
                </Table.Td>
                <Table.Td>
                    <TextInput
                        value={row?.batchNumber}
                        maxLength={20}
                        onChange={(e) => handleChange('batchNumber', e.currentTarget.value)}
                        error={row?.batchNumber ? undefined : errors.batchNumber}
                    />
                </Table.Td>
                <Table.Td>
                    <TextInput
                        value={row?.trackingNumber}
                        maxLength={20}
                        onChange={(e) => handleChange('trackingNumber', e.currentTarget.value)}
                        error={row?.trackingNumber ? undefined : errors.trackingNumber}
                    />
                </Table.Td>
                <Table.Td>
                    <CalendarDateInput
                        placeholder={t('placeHolder.dateFormat')}
                        value={row?.expirationDate}
                        onChange={(date) => handleChange('expirationDate', date)}
                        error={errors.expirationDate}
                        tabIndex={-1}
                    />
                </Table.Td>
                <Table.Td>
                    <NumberInput
                        placeholder={t('modal.testKitModal.enterPrice')}
                        onChange={(value) => handleChange('price', value)}
                        value={row.price}
                        thousandSeparator=","
                        decimalScale={2}
                        fixedDecimalScale
                        suffix="$"
                        hideControls
                        tabIndex={-1}
                        miw={80}
                    />
                </Table.Td>
                <Table.Td>
                    <ActionIcon tabIndex={-1} variant="subtle" color="gray" onClick={() => onClickDelete(row.id!)}>
                        <IconTrash size={18} />
                    </ActionIcon>
                </Table.Td>
            </Table.Tr>
        );
    }),
);

export default TestKitFormRow;
